// ABOUTME: Agent Terms and Conditions modal component.
// ABOUTME: Renders the full T&C text for agents and an Accept button.
import React, { useState, useEffect } from 'react';
import { Modal } from '../../atoms/modal';
import { definitions, purposes, dataRetentionPolicy } from './tables';
import {
  Box,
  Button,
  Heading,
  Flex,
  Table,
  Tr,
  Th,
  Td,
  TableContainer,
  Alert,
  AlertIcon,
  AlertDescription,
  UnorderedList,
  ListItem,
} from '@chakra-ui/react';

interface IProps {
  onAcceptTerms: () => void;
  error?: string;
  isLoading?: boolean;
}

interface IChildren {
  children: React.ReactNode;
}

interface ITableColumn {
  isHeader?: boolean;
  isMain?: boolean;
  content: string;
}

interface ITableRow {
  columns: ITableColumn[];
}

const constructColumns = (row: ITableColumn[], rowIndex: number): React.ReactNode[] => {
  const result: React.ReactNode[] = [];
  for (const [index, column] of row.entries()) {
    if (column.isHeader) {
      result.push(
        <Th key={`header-table-column-${rowIndex}-${index}`}>
          {column.content.split('\n').map((c: string) => (
            <div>{c}</div>
          ))}
        </Th>,
      );
    } else if (column.isMain) {
      result.push(
        <Th key={`main-table-column-${rowIndex}-${index}`} fontSize="md" verticalAlign="top" px={0} py={4}>
          {column.content.split('\n').map((c: string) => (
            <div>{c}</div>
          ))}
        </Th>,
      );
    } else {
      result.push(
        <Td key={`table-column-${rowIndex}-${index}`}>
          {column.content.split('\n').map((c: string) => (
            <div>{c}</div>
          ))}
        </Td>,
      );
    }
  }
  return result;
};

const constructRows = (table: ITableRow[]): React.ReactNode[] => {
  const result: React.ReactNode[] = [];
  for (const [index, row] of table.entries()) {
    result.push(<Tr key={`table-row-${index}`}>{constructColumns(row.columns, index)}</Tr>);
  }
  return result;
};

const constructTable = (table: ITableRow[]): React.ReactNode => {
  return (
    <>
      <Table variant="simple">{constructRows(table)}</Table>
    </>
  );
};

const AgentTermsModal: React.FC<IProps> = (props: IProps) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
    const target = event.currentTarget;
    const threshold = 10; // Allow 10px tolerance for scrolling to bottom
    const hasReachedBottom = target.scrollHeight - target.scrollTop - target.clientHeight <= threshold;

    if (hasReachedBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  useEffect(() => {
    // Check if content is not scrollable (fully visible)
    const modalBody = document.querySelector('[data-selector="agent-terms-modal"] .chakra-modal__body');
    if (modalBody) {
      const isContentFullyVisible = modalBody.scrollHeight <= modalBody.clientHeight;
      if (isContentFullyVisible) {
        setHasScrolledToBottom(true);
      }
    }
  }, []);

  return (
    <Modal
      id={'agent-terms'}
      show={true}
      title={'Terms and Conditions for Agents'}
      canClose={false}
      onClose={() => {}}
      size="xl"
      scrollBehaviour="inside"
      modalBodyProps={{
        onScroll: handleScroll,
      }}
      footer={
        <Flex direction="column" align="center" gap={3} width="100%">
          {props.error && (
            <Alert status="error">
              <AlertIcon />
              <AlertDescription>There was a problem recording your acceptance. Please try again.</AlertDescription>
            </Alert>
          )}
          <Button
            id={'accept-agent-terms-button'}
            onClick={props.onAcceptTerms}
            variant="solid"
            size="lg"
            isLoading={props.isLoading}
            isDisabled={props.isLoading || !hasScrolledToBottom}
          >
            Accept
          </Button>
        </Flex>
      }
    >
      <ParagraphTitle>1. Background</ParagraphTitle>
      <Paragraph>
        <NumberColumn>1.1</NumberColumn>
        <TextColumn>
          Enroly provides universities, colleges and other high education institutions a subscription service which
          allows them to access tools to manage the recruitment process for international students (“CAS Shield
          Services”).
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>1.2</NumberColumn>
        <TextColumn>
          The CAS Shield Services comprise online software applications provided by Enroly (“CAS Shield Software”) and
          documents provided by Enroly in connection with the CAS Shield Services, in either printed text or
          machine-readable form, including the technical documentation and program specification (“CAS Shield
          Documentation”).
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>1.3</NumberColumn>
        <TextColumn>
          Enroly is the owner and licensor of the CAS Shield Software and the CAS Shield Documentation and the CAS
          Shield Services are provided to each university, college and high education institutions under a contract
          between Enroly and each of them (“CAS Shield Contract”).
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>1.4</NumberColumn>
        <TextColumn>
          Certain universities, colleges and high education institutions (“Institutions”) have informed Enroly that You
          have been appointed by the Institutions and/or some of their international students (“Students”) to act as
          their authorised agent in connection with the management of the recruitment process of the Students using the
          CAS Shield Services (“Agency Services”).
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>1.5</NumberColumn>
        <TextColumn>
          Although Enroly is not a party to Your agreement with the Institution and/or the Student for the supply of the
          Agency Services, You need to access the CAS Shield Services to provide the Agency Services to the Institutions
          and/or the Students.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>1.6</NumberColumn>
        <TextColumn>
          These terms and conditions (“TCs for Agents”) apply whenever You access the CAS Shield Services in order to
          provide the Agency Services to the Institutions and/or Students.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>2. Your Contract with Enroly</ParagraphTitle>
      <Paragraph>
        <NumberColumn>2.1</NumberColumn>
        <TextColumn>
          When a Institution and/or a Student engages You to provide the Agency Services to the Institutions and/or to a
          Student, the Institution informs Enroly about this appointment via CAS Shield Software and authorises Enroly
          to grant You access to its CAS Shield Services in accordance with these TCs for Agents.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>2.2</NumberColumn>
        <TextColumn>
          When Enroly receives the information and authorisation referred to in clause 2.1, Enroly will notify You via
          CAS Shield Software and when You receive this notification a contract will be formed between You and Enroly
          for the access of the CAS Shield Services in relation to those Institutions and/or Students in accordance with
          these TCs for Agents (“Contract”).
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>3. Your access to CAS Shield Services</ParagraphTitle>
      <Paragraph>
        <NumberColumn>3.1</NumberColumn>
        <TextColumn>
          After the Contract is formed, Enroly will grant You access to the CAS Shield Services related to those
          Institutions and/or Students that Enroly has been informed about in accordance with clause 2.1. You will also
          have access to a dashboard where You will be able to see all Institutions and/or Students that engaged You to
          provide the Agency Services to them.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>3.2</NumberColumn>
        <TextColumn>
          Your access to the CAS Shield Services in relation to any Institution and/or Student will remain valid for as
          long as You are engaged by the Institution to provide the Agency Services to the Institution and/or a Student
          and provided that You comply with these TCs for Agents.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>3.3</NumberColumn>
        <TextColumn>
          When an Institution informs Enroly that You are no longer engaged to provide the Agency Services, Enroly will
          automatically cancel Your access to the CAS Shield Services in relation to that Institution and/or Student.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>3.4</NumberColumn>
        <TextColumn>
          For the purposes of authorising You to access their CAS Shield Services, the Institutions have the right to
          inform Enroly at any time that You are no longer engaged to provide the Agency Services to them and/or the
          Students and Enroly shall not be obliged to request Your approval or inform You that Your access to CAS Shield
          Services related to the Institutions is cancelled under clause 3.3.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>3.5</NumberColumn>
        <TextColumn>
          If Your access to the CAS Shield Services related to a certain Institution and/or Student is cancelled by the
          request of that Institution, such cancellation will not affect Your access to the CAS Shield Services related
          to the Institutionsand/or Students not included in that request.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>3.6</NumberColumn>
        <TextColumn>
          If You or an Authorised User breach any provision of these TCs for Agents, Enroly shall have the right to
          suspend Your access to the CAS Shield Services in relation to all Institutions and Students that have engaged
          You to provide the Agency Services and notify them of the relevant breach.
          <SubParagraph>
            <NumberColumn>3.6.1</NumberColumn>
            <TextColumn>
              Where the breach relates to, or Enroly reasonably suspects involves:
              <SubParagraph>
                <NumberColumn>(a)</NumberColumn>
                <TextColumn>fraud, attempted fraud, misrepresentation or deception;</TextColumn>
              </SubParagraph>
              <SubParagraph>
                <NumberColumn>(b)</NumberColumn>
                <TextColumn>submission of altered, falsified or misleading documents;</TextColumn>
              </SubParagraph>
              <SubParagraph>
                <NumberColumn>(c)</NumberColumn>
                <TextColumn>interview fraud, impersonation, or identity misuse;</TextColumn>
              </SubParagraph>
              <SubParagraph>
                <NumberColumn>(d)</NumberColumn>
                <TextColumn>
                  a security compromise, including unauthorised access or attempted circumvention of authentication
                  controls;
                </TextColumn>
              </SubParagraph>
              <SubParagraph>
                <NumberColumn>(e)</NumberColumn>
                <TextColumn>a personal data breach or breach of data protection obligations; or</TextColumn>
              </SubParagraph>
              <SubParagraph>
                <NumberColumn>(f)</NumberColumn>
                <TextColumn>the sharing, transfer, or misuse of login credentials,</TextColumn>
              </SubParagraph>
              such breach shall be deemed a material breach, and Enroly may immediately suspend or terminate Your access
              to the CAS Shield Services without any requirement to provide notice or a cure period.
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>3.6.2</NumberColumn>
            <TextColumn>
              Any suspension under clause 3.6.1 shall remain in place unless and until Enroly, acting reasonably,
              determines that access may be reinstated or the relevant Institution confirms otherwise.
            </TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>3.7</NumberColumn>
        <TextColumn>
          The suspension and cancellation referred to in clause 3.6 are in addition to and without prejudice of the
          remedies set out in clause 5.5.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>3.8</NumberColumn>
        <TextColumn>
          On cancellation of Your access to CAS Shield Services in relation to an Institution:
          <SubParagraph>
            <NumberColumn>3.8.1</NumberColumn>
            <TextColumn>
              all licences granted under these TCs for Agents in connection with such access shall immediately terminate
              and You shall immediately cease access those CAS Shield Services;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>3.8.2</NumberColumn>
            <TextColumn>
              You shall return and make no further use of any equipment, property, documents and other items (and all
              copies of them) belonging to Enroly; and
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>3.8.3</NumberColumn>
            <TextColumn>
              Enroly may destroy or otherwise dispose of any of the data You supplied to Enroly in connection with these
              TCs for Agents which is in Enroly’s possession.
            </TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>4. Licence to Access the CAS Shield Services</ParagraphTitle>
      <Paragraph>
        <NumberColumn>4.1</NumberColumn>
        <TextColumn>
          When You are granted access to CAS Shield Services under clause 3, Enroly grants to You for the Term a royalty
          free, non-exclusive, non-transferable, non-sublicensable right to permit individuals that are Your employees,
          agents, independent contractors and authorised third parties (“Authorised Users”) to use the CAS Shield
          Services for the sole and exclusive purposes of providing the Agency Services to the Institutions and/or
          Students that appointed You as their authorised agent (“Authorised Use”).
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>4.2</NumberColumn>
        <TextColumn>
          The rights provided under this clause 4 are granted to You only and not to any other affiliate.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>5. No Virus and No Modification</ParagraphTitle>
      <Paragraph>
        <NumberColumn>5.1</NumberColumn>
        <TextColumn>
          You shall and You shall ensure that any and all Authorised Users shall not access, store, distribute or
          transmit any Viruses, or any material during the course of the Authorised Use that:
          <SubParagraph>
            <NumberColumn>5.1.1</NumberColumn>
            <TextColumn>
              is unlawful, harmful, threatening, defamatory, obscene, infringing, harassing or racially or ethnically
              offensive;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>5.1.2</NumberColumn>
            <TextColumn>facilitates illegal activity;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>5.1.3</NumberColumn>
            <TextColumn>depicts sexually explicit images;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>5.1.4</NumberColumn>
            <TextColumn>promotes unlawful violence;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>5.1.5</NumberColumn>
            <TextColumn>
              is discriminatory based on race, gender, colour, religious belief, sexual orientation, disability; or
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>5.1.6</NumberColumn>
            <TextColumn>is otherwise illegal or causes damage or injury to any person or property.</TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>5.2</NumberColumn>
        <TextColumn>
          For the purposes of these TCs for Agents and the Contract, Virus means any thing or device (including any
          software, code, file or programme) which may:
          <SubParagraph>
            <NumberColumn>5.2.1</NumberColumn>
            <TextColumn>
              prevent, impair or otherwise adversely affect the operation of any computer software, hardware or network,
              any telecommunications service, equipment or network or any other service or device;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>5.2.2</NumberColumn>
            <TextColumn>
              prevent, impair or otherwise adversely affect access to or the operation of any programme or data,
              including the reliability of any programme or data (whether by rearranging, altering or erasing the
              programme or data in whole or part or otherwise); or
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>5.2.3</NumberColumn>
            <TextColumn>
              adversely affect the user experience, including worms, trojan horses, viruses and other similar things or
              devices.
            </TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>5.3</NumberColumn>
        <TextColumn>
          You and any and all Authorised Users shall not (except as may be allowed by any applicable law which is
          incapable of exclusion by agreement between the parties and except to the extent expressly permitted under
          this agreement):
          <SubParagraph>
            <NumberColumn>5.3.1</NumberColumn>
            <TextColumn>
              attempt to copy, modify, duplicate, create derivative works from, frame, mirror, republish, download,
              display, transmit, or distribute all or any portion of the CAS Shield Software and/or CAS Shield
              Documentation (as applicable) in any form or media or by any means;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>5.3.2</NumberColumn>
            <TextColumn>
              attempt to de-compile, reverse compile, disassemble, reverse engineer or otherwise reduce to
              human-perceivable form all or any part of the CAS Shield Software;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>5.3.3</NumberColumn>
            <TextColumn>
              access all or any part of the CAS Shield Services in order to build a product or service which competes
              with the CAS Shield Services; or
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>5.3.4</NumberColumn>
            <TextColumn>
              license, sell, rent, lease, transfer, assign, distribute, display, disclose, commercially exploit or
              otherwise make the CAS Shield Services available to any third party except the Authorised Users.
            </TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>5.4</NumberColumn>
        <TextColumn>
          You shall use all reasonable endeavours to prevent any unauthorised access to, or use of, the CAS Shield
          Services. In the event of any such unauthorised access or use, You shall promptly notify Enroly. Any breach of
          this clause shall be treated as a security breach for the purposes of clause 3.6
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>5.5</NumberColumn>
        <TextColumn>
          Enroly reserves the right, without liability or prejudice to its other rights to You, to disable Your and the
          Authorised Users’ access to any material that breaches the provisions of this clause.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>6. Your Representations, Warranties and Indemnities</ParagraphTitle>
      <Paragraph>
        <NumberColumn>6.1</NumberColumn>
        <TextColumn>
          You represent and warrant that You to Enroly that:
          <SubParagraph>
            <NumberColumn>6.1.1</NumberColumn>
            <TextColumn>
              You have been engaged by the Institutions and the Students to provide the Agency Services to them;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>6.1.2</NumberColumn>
            <TextColumn>
              You will immediately report to Enroly if an Institution and/or Student that appears on Your dashboard has
              not engaged You to provide the Agency Services to them so that Enroly can cancel Your access to the CAS
              Shield Services related to any such Institution or Student;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>6.1.3</NumberColumn>
            <TextColumn>
              You will not access the CAS Shield Services nor provide the Agency Services unless in accordance with Your
              appointment as an authorised agent of the Insitutions and/or the Students and within the powers granted to
              You under such appointment; and
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>6.1.4</NumberColumn>
            <TextColumn>
              Your network and systems comply with the relevant specifications provided by Enroly from time to time.
            </TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>6.2</NumberColumn>
        <TextColumn>
          You represent and warranty to Enroly that You and any and all Authorise Users:
          <SubParagraph>
            <NumberColumn>6.2.1</NumberColumn>
            <TextColumn>
              shall not allow any person that is not an Authorised User to access the CAS Shield Services;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>6.2.2</NumberColumn>
            <TextColumn>
              In the issue of accessing the platform;
              <SubParagraph>
                <NumberColumn>6.2.2.1</NumberColumn>
                <TextColumn>
                  shall keep secure and confidential any password and/or other login credentials used to access the CAS
                  Shield Services, which are issued on an individual, non-transferable basis. Shared, generic, or group
                  logins are not supported and are strictly prohibited.
                </TextColumn>
              </SubParagraph>
              <SubParagraph>
                <NumberColumn>6.2.2.2</NumberColumn>
                <TextColumn>
                  shall ensure that each Authorised User accesses the CAS Shield Services only using their own unique
                  credentials and shall not permit any credential sharing, reuse, or circumvention of authentication
                  controls. Any breach of this obligation may result in immediate suspension or termination of access
                  and shall be treated as a security breach for the purposes of clause 3.6
                </TextColumn>
              </SubParagraph>
              <SubParagraph>
                <NumberColumn>6.2.2.3</NumberColumn>
                <TextColumn>
                  shall comply with all security, authentication, and access control measures implemented by Enroly from
                  time to time, including mandatory multi-factor authentication (MFA), device verification, and identity
                  validation requirements. Failure to comply with such measures shall constitute a material breach and
                  may result in immediate suspension of access.
                </TextColumn>
              </SubParagraph>
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>6.2.3</NumberColumn>
            <TextColumn>
              will provide Enroly with:
              <SubParagraph>
                <NumberColumn>6.2.3.1</NumberColumn>
                <TextColumn>all necessary co-operation;</TextColumn>
              </SubParagraph>
              <SubParagraph>
                <NumberColumn>6.2.3.2</NumberColumn>
                <TextColumn>all necessary access to such information as may be required by Enroly;</TextColumn>
              </SubParagraph>
              <SubParagraph>
                <NumberColumn>6.2.3.3</NumberColumn>
                <TextColumn>
                  access to all the data inputted by You, the Authorised Users or Enroly’s on Your behalf for the
                  purpose of accessing the CAS Shield Services; and
                </TextColumn>
              </SubParagraph>
              <SubParagraph>
                <NumberColumn>6.2.3.4</NumberColumn>
                <TextColumn>access to information and configuration services.</TextColumn>
              </SubParagraph>
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>6.2.4</NumberColumn>
            <TextColumn>
              will comply with all applicable laws and regulations that apply to the Agency Services;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>6.2.5</NumberColumn>
            <TextColumn>
              will carry out all other responsibilities set out in these TCs for Agents in a timely and efficient
              manner; and
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>6.2.6</NumberColumn>
            <TextColumn>
              obtain and shall maintain all necessary licences, consents, and permissions necessary for Enroly, its
              contractors and agents to perform their obligations under these TCs for Agents and the Contract.
            </TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>6.3</NumberColumn>
        <TextColumn>
          You are solely responsible for:
          <SubParagraph>
            <NumberColumn>6.3.1</NumberColumn>
            <TextColumn>
              procuring and maintaining Your network connections and telecommunications links from Your systems to
              Enroly's data centres;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>6.3.2</NumberColumn>
            <TextColumn>
              for all problems, conditions, delays, delivery failures and all other loss or damage arising from or
              relating to Your network connections or telecommunications links or caused by the internet.
            </TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>6.4</NumberColumn>
        <TextColumn>
          You shall defend, indemnify and hold Enroly harmless for any loss, damage, claim or demand of whatever nature
          incurred or suffered by Enroly as a result of or in connection with a breach or failure by You or any
          Authorised User to comply with any provision of these TCs for Agents (including any breach of clauses 6.1, 6.2
          and 6.3.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>6.5</NumberColumn>
        <TextColumn>
          The obligations and warranties set out in this clause 6 are in addition and without prejudice to the
          obligations and warranties set out in clause 5 and Enroly&apos;s right to suspend and cancel Your access to
          the CAS Shield Services in accordance with clause 3.6.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>6.6</NumberColumn>
        <TextColumn>
          Reporting, Cooperation and Investigations: You shall, and shall ensure that all Authorised Users shall:
          <SubParagraph>
            <NumberColumn>6.6.1</NumberColumn>
            <TextColumn>
              promptly notify Enroly of any actual or suspected: (a) fraud, attempted fraud, misrepresentation or
              deception; (b) security incident, unauthorised access, or credential compromise; or (c) personal data
              breach or suspected breach of data protection obligations, relating to the CAS Shield Services or any
              Student, Institution, or application processed through the platform.
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>6.6.2</NumberColumn>
            <TextColumn>
              fully and promptly cooperate with Enroly in connection with any investigation, audit, or enquiry relating
              to the matters set out above, including by: (a) providing timely, complete, and accurate explanations and
              information; (b) making relevant personnel available for interview or clarification; and (c) assisting
              Enroly in responding to requests from Institutions, regulators, or other competent authorities where
              applicable.
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>6.6.3</NumberColumn>
            <TextColumn>
              preserve all records, documents, communications, and data (in whatever form) which may be relevant to any
              such investigation and shall not delete, alter, conceal, or destroy any such materials without
              Enroly&apos;s prior written consent.
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>6.6.4</NumberColumn>
            <TextColumn>
              acknowledge that failure to comply with this clause 6.6 shall constitute a material breach of these TCs
              for Agents.
            </TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>7. Your agreement with the Institutions and/or the Students</ParagraphTitle>
      <Paragraph>
        <NumberColumn>7.1</NumberColumn>
        <TextColumn>
          Enroly shall not be obliged to verify the terms and conditions of Your agreement or arrangements with the
          Institutions and/or the Students.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>7.2</NumberColumn>
        <TextColumn>
          Therefore, Enroly shall not be liable for any loss, damage, claim or demand of whatever nature incurred or
          suffered by You as a result of or in connection with a breach or failure by a Institution and/or Student to
          comply with any provision of the agreements or arrangements You have with them.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>8. Enroly’s Obligations</ParagraphTitle>
      <Paragraph>
        <NumberColumn>8.1</NumberColumn>
        <TextColumn>
          Enroly shall grant You access to the CAS Shield Services in accordance with these TCs for Agents and the
          Contract.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>8.2</NumberColumn>
        <TextColumn>
          Enroly shall:
          <SubParagraph>
            <NumberColumn>8.2.1</NumberColumn>
            <TextColumn>
              maintain all necessary licences, consents, and permissions necessary for the performance of its
              obligations under these TCs for Agents and the Contract;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>8.2.2</NumberColumn>
            <TextColumn>co-operate with You in all matters relating to the Agency Services; and</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>8.2.3</NumberColumn>
            <TextColumn>
              comply with all applicable laws, statutes, regulations and codes from time to time in force.
            </TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>8.3</NumberColumn>
        <TextColumn>
          The obligation set out in clause 8.2 shall not apply if any non-conformance is caused by use of Your or the
          Authorised User’s failure to comply with these TCs for Agents.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>9. Support Services Policy</ParagraphTitle>
      <Paragraph>
        <NumberColumn>9.1</NumberColumn>
        <TextColumn>
          Enroly shall make the CAS Shield Services available in accordance with the terms and conditions set out in the
          CAS Shield Contracts.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>9.2</NumberColumn>
        <TextColumn>
          Enroly shall have no obligation under these TCs for Agents or the Contract to maintain the CAS Shield Services
          available to You or to any Authorised User.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>9.3</NumberColumn>
        <TextColumn>
          Notwithstanding clause 9.2, if You notice any problem when You access or try to access CAS Shield, You shall
          immediately report it to Enroly’s Procedures Technical Support Desk system which operates 09.00 – 17.30 on
          Business Days. You can access the Enroly’s Procedures Technical Support Desk via:
          <SubParagraph>
            <DetailColumn>Email:</DetailColumn>
            <TextColumn>agents@cas-shield.com</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <DetailColumn>Live chat:</DetailColumn>
            <TextColumn>Blue chat button on bottom left of screen</TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>10. Data Protection</ParagraphTitle>
      <Paragraph>
        <NumberColumn>10.1</NumberColumn>
        <TextColumn>
          Any data exchanged between You and Enroly shall be subject to the Enroly Data Protection and Data Sharing
          Policy which can be found at the end of these TCs for Agents.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>10.2</NumberColumn>
        <TextColumn>
          In addition to the provisions of the Enroly Data Protection and Data Sharing Policy, You consent that Enroly
          collects, holds and processes Your and the Authorised User’s data (including personal data) for purposes not
          directly related to the grant of access to the CAS Shield Services under these TCs, including for the purposes
          of conducting performance reviews and marketing intelligence analysis and reports that can be shared with the
          Institutions, the Students and other third parties.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>10.3</NumberColumn>
        <TextColumn>
          You represent and warranty to Enroly that You are duly authorised by the Authorised Users to give the consent
          referred to in clause 10.2. You shall defend, indemnify and hold Enroly harmless for any loss, damage, claim
          or demand of whatever nature (including claims brought by the Authorised Users) incurred or suffered by Enroly
          as a result of or in connection with Your failure to comply with this warranty.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>11. Intellectual Property</ParagraphTitle>
      <Paragraph>
        <NumberColumn>11.1</NumberColumn>
        <TextColumn>
          You acknowledge and agree that Enroly and/or its licensors own all intellectual property rights in the CAS
          Shield Services. Except for the uses expressly authorised under these TCS for Agents, You shall not have any
          intellectual property rights to, under or in any part of the CAS Shield Services, including rights to, under
          or in, any patents, copyright, database right, trade secrets, trade names, trade marks (whether registered or
          unregistered), or any other rights or licences in respect of the Services.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>12. Confidentiality</ParagraphTitle>
      <Paragraph>
        <NumberColumn>12.1</NumberColumn>
        <TextColumn>
          You and/or Enroly may be given access to Confidential Information from the other party in order to perform its
          obligations under these TCs for Agents and the Contract.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>12.2</NumberColumn>
        <TextColumn>
          A party's Confidential Information includes:
          <SubParagraph>
            <NumberColumn>12.2.1</NumberColumn>
            <TextColumn>information that labelled as proprietary or confidential;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>12.2.2</NumberColumn>
            <TextColumn>information that is proprietary or confidential by its nature;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>12.2.3</NumberColumn>
            <TextColumn>Your and/or Enroly’s business and affairs;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>12.2.4</NumberColumn>
            <TextColumn>
              existing and potential customers, clients, investors, contractors, suppliers, contacts or business
              partners of the party;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>12.2.5</NumberColumn>
            <TextColumn>plans, intentions, projects or market opportunities of the party;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>12.2.6</NumberColumn>
            <TextColumn>
              Your and/or Enroly’s researches, operations, processes, product information, know-how, technical
              information, designs, trade secrets, applications or software, including all final and/or intermediate
              development output;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>12.2.7</NumberColumn>
            <TextColumn>
              any information, findings, results, data or analysis derived from Confidential Information, including all
              final and/or intermediate development output; and
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>12.2.8</NumberColumn>
            <TextColumn>any detail of the CAS Shield Services.</TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>12.3</NumberColumn>
        <TextColumn>
          Confidential Information does not include nor shall be deemed to include information that:
          <SubParagraph>
            <NumberColumn>12.3.1</NumberColumn>
            <TextColumn>
              is or becomes publicly known other than through any act or omission of the receiving party;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>12.3.2</NumberColumn>
            <TextColumn>was in the other party's lawful possession before the disclosure;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>12.3.3</NumberColumn>
            <TextColumn>
              is lawfully disclosed to the receiving party by a third party without restriction on disclosure; or
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>12.3.4</NumberColumn>
            <TextColumn>
              is independently developed by the receiving party, which independent development can be shown by written
              evidence.
            </TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>12.4</NumberColumn>
        <TextColumn>
          Subject to clause 12.5, each party shall hold the other's Confidential Information in confidence and shall not
          make the other's Confidential Information available to any third party, or use the other's Confidential
          Information for any purpose other than the exercise of rights and performance of obligations under these TCs
          for Agents and the Contract.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>12.5</NumberColumn>
        <TextColumn>
          A party may disclose Confidential Information to the extent such Confidential Information is required to be
          disclosed by law, by any governmental or other regulatory authority or by a court or other authority of
          competent jurisdiction, provided that, to the extent it is legally permitted to do so, it gives the other
          party as much notice of such disclosure as possible and, where notice of disclosure is not prohibited and is
          given in accordance with this clause, it takes into account the reasonable requests of the other party in
          relation to the content of such disclosure.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>12.6</NumberColumn>
        <TextColumn>
          No party shall make, or permit any person to make, any public announcement concerning these TCs for Agents or
          the Contract without the prior written consent of the other parties (such consent not to be unreasonably
          withheld or delayed), except as required by law, any governmental or regulatory authority (including, without
          limitation, any relevant securities exchange), any court or other authority of competent jurisdiction.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>12.7</NumberColumn>
        <TextColumn>
          The confidentiality obligations set out in this clause shall survive the cancellation of Your access to the
          CAS Shield Services under these TCs for Agents.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>13. Use of Confidential Information</ParagraphTitle>
      <Paragraph>
        <NumberColumn>13.1</NumberColumn>
        <TextColumn>
          You and Enroly shall not use or otherwise exploit nor allow any third party to use or otherwise exploit any
          Confidential Information or any part of it in any way except for the exercise of Your rights and/or the
          performance of Your obligations under these TCs for Agents and the Contract.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>13.2</NumberColumn>
        <TextColumn>
          You shall not use or otherwise exploit nor allow any third party to use or otherwise exploit any Confidential
          Information to develop, create or in any way produce any plans, intentions, projects, market opportunities,
          researches, operations, processes, product information, know-how, technical information, designs, trade
          secrets, applications, software, database or analysis identical or in any way similar or competing with any of
          the CAS Shield Services.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>13.3</NumberColumn>
        <TextColumn>
          You shall not engage, negotiate, discuss with and/or enter into any agreement or arrangement with any third
          party with regards to services similar to the CAS Shield Services without Enroly’s previous approval in
          writing.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>13.4</NumberColumn>
        <TextColumn>
          The restrictions set out in this clause 13 are considered reasonable by You and Enroly. Nevertheless, in the
          event that any such restriction shall be found to be void or voidable but would be valid if some part or some
          parts thereof were deleted or the period or area of application reduced, such restriction shall apply with
          such modification as may be necessary to make it valid and effective.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>14. Limitation of Liability</ParagraphTitle>
      <Paragraph>
        <NumberColumn>14.1</NumberColumn>
        <TextColumn>
          You assume sole responsibility for results obtained from Your and any Authorised User's access to CAS Shields,
          and for conclusions drawn from such access.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>14.2</NumberColumn>
        <TextColumn>
          All warranties, representations, conditions and all other terms of any kind whatsoever implied by statute or
          common law are, to the fullest extent permitted by applicable law, excluded from these TCs for Agents and the
          Contract.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>14.3</NumberColumn>
        <TextColumn>The access to CAS Shield Services is provided to You on an "as is" basis.</TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>14.4</NumberColumn>
        <TextColumn>
          Enroly shall have no liability for any damage caused by errors or omissions in any information, instructions
          or scripts provided to Enroly by You and/or the Institutions, or any actions taken by Enroly at Your and/or
          the Institutions’ direction;
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>14.5</NumberColumn>
        <TextColumn>
          Nothing in these TCs for Agents or in the Contract excludes Enroly’s liability
          <SubParagraph>
            <NumberColumn>14.5.1</NumberColumn>
            <TextColumn>for death or personal injury caused by Enroly's negligence; or</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>14.5.2</NumberColumn>
            <TextColumn>for fraud or fraudulent misrepresentation.</TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>14.6</NumberColumn>
        <TextColumn>
          Enroly shall not be liable whether in tort (including for negligence or breach of statutory duty), contract,
          misrepresentation, restitution or otherwise for any (i) loss of profits, loss of business, depletion of
          goodwill and/or similar losses, (ii) pure economic loss, or (iii) for any special, indirect or consequential
          loss, costs, damages, charges or expenses however arising under these TCs for Agents or the Contract.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>14.7</NumberColumn>
        <TextColumn>
          Enroly’s total aggregate liability in contract, tort (including negligence or breach of statutory duty),
          misrepresentation, restitution or otherwise, arising in connection with the performance or contemplated
          performance of these TCs for Agents or Contract shall be limited to £100.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>15. Force Majeure</ParagraphTitle>
      <Paragraph>
        <NumberColumn>15.1</NumberColumn>
        <TextColumn>
          Neither party shall in any circumstances be in breach of these TCs for Agents or the Contract nor liable for
          delay in performing, or failure to perform, any of its obligations under these TCs for Agents and the Contract
          if such delay or failure results from events, circumstances or causes beyond its reasonable control.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>16. Fraud, Misrepresentation and Integrity</ParagraphTitle>
      <Paragraph>
        <NumberColumn>16.1</NumberColumn>
        <TextColumn>
          The Agent shall not, and shall ensure that its Authorised Users and any Students acting at its direction do
          not, engage in any act or omission which is fraudulent, misleading, deceptive, or intended to misrepresent any
          information submitted through or in connection with the CAS Shield Services. This includes, without
          limitation:
          <UnorderedList>
            <ListItem>the submission of altered, falsified or misleading documents;</ListItem>
            <ListItem>impersonation or attempted impersonation of a Student or third party;</ListItem>
            <ListItem>interview fraud or coaching intended to mislead;</ListItem>
            <ListItem>the concealment or omission of material information; and</ListItem>
            <ListItem>
              any attempt to circumvent identity verification, security controls, or authentication measures.
            </ListItem>
          </UnorderedList>
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>16.2</NumberColumn>
        <TextColumn>
          Any attempted fraud or suspected fraudulent activity shall be treated as a material breach of these TCs.
          Enroly reserves the right, acting reasonably, to:
          <UnorderedList>
            <ListItem>immediately suspend or terminate the Agent&apos;s access to the CAS Shield Services;</ListItem>
            <ListItem>notify the relevant Institution(s);</ListItem>
            <ListItem>
              share relevant information with compliance teams, regulators, or law enforcement where required or
              appropriate; and
            </ListItem>
            <ListItem>permanently restrict future access to the CAS Shield platform.</ListItem>
          </UnorderedList>
          The Agent acknowledges that such actions may be taken without prior notice and without any obligation to
          provide a cure period.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>17. Anti-Bribery Provisions</ParagraphTitle>
      <Paragraph>
        <NumberColumn>17.1</NumberColumn>
        <TextColumn>
          You and Enroly shall act in good faith with respect to the provisions of these TCs for Agents and shall
          conduct yourselves in accordance with the spirit and anti-bribery provisions of the Bribery Act 2010. Each
          Party shall ensure proportionate compliance with this clause, taking into account factors including, but not
          limited to, the size and nature of its business; the markets and territories in which it operates; and the
          nature of any other parties with which it transacts including, but not limited to, contractors, agents,
          suppliers, service providers and clients. In particular, neither party shall offer, request, agree to receive
          or accept anything that may, under the relevant provisions of the Bribery Act 2010, constitute a bribe.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>18. Variation</ParagraphTitle>
      <Paragraph>
        <NumberColumn>18.1</NumberColumn>
        <TextColumn>
          Enroly may revise these TCs for Agents from time to time, including in response to changes in relevant laws
          and other regulatory requirements.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>18.2</NumberColumn>
        <TextColumn>
          If Enroly changes these TCs for Agents as they relate to Your rights and obligations, Enroly will give You
          reasonable advance notice of the changes.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>19. Waiver</ParagraphTitle>
      <Paragraph>
        <NumberColumn>19.1</NumberColumn>
        <TextColumn>
          No failure or delay by a party to exercise any right or remedy provided under these TCs for Agents, the
          Contract or by law shall constitute a waiver of that or any other right or remedy, nor shall it preclude or
          restrict the further exercise of that or any other right or remedy. No single or partial exercise of such
          right or remedy shall preclude or restrict the further exercise of that or any other right or remedy.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>20. Rights and Remedies</ParagraphTitle>
      <Paragraph>
        <NumberColumn>20.1</NumberColumn>
        <TextColumn>
          Except as expressly provided in these TCs for Agents or in the Contract, the rights and remedies provided
          under these TCs for Agents are in addition to, and not exclusive of, any rights or remedies provided by law.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>21. Severance</ParagraphTitle>
      <Paragraph>
        <NumberColumn>21.1</NumberColumn>
        <TextColumn>
          If any provision (or part of a provision) of these TCs for Agents is found by any court or administrative body
          of competent jurisdiction to be invalid, unenforceable or illegal, the other provisions shall remain in force.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>21.2</NumberColumn>
        <TextColumn>
          If any invalid, unenforceable or illegal provision would be valid, enforceable or legal if some part of it
          were deleted, the provision shall apply with whatever modification is necessary to give effect to the
          commercial intention of the parties.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>22. Entire Agreement</ParagraphTitle>
      <Paragraph>
        <NumberColumn>22.1</NumberColumn>
        <TextColumn>
          These TCs for Agents (which for the avoidance of doubt includes all documents referred to in clause 1.3 and
          any documents expressly referred to in such documents) constitutes the entire agreement between the parties
          and supersedes and extinguishes all previous agreements, promises, assurances, warranties, representations and
          understandings between them, whether written or oral, relating to its subject matter.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>22.2</NumberColumn>
        <TextColumn>
          Each party acknowledges that in entering into the Contract it does not rely on, and shall have no remedies in
          respect of, any statement, representation, assurance or warranty (whether made innocently or negligently) that
          is not set out in these TCs for Agents.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>23. Assignment</ParagraphTitle>
      <Paragraph>
        <NumberColumn>23.1</NumberColumn>
        <TextColumn>
          You shall not, without Enroly&apos;s prior written consent, assign, transfer, charge, sub-contract or deal in
          any other manner with all or any of its rights or obligations under these TCs for Agents or the Contract.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>23.2</NumberColumn>
        <TextColumn>
          Enroly may at any time assign, transfer, charge, sub-contract or deal in any other manner with all or any of
          its rights or obligations under these TCs for Agents or the Contract.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>24. No Partnership or Agency</ParagraphTitle>
      <Paragraph>
        <NumberColumn>24.1</NumberColumn>
        <TextColumn>
          Nothing in these TCs for Agents or in the Contract is intended to or shall operate to create a partnership
          between the parties, or authorise either party to act as agent for the other, and neither party shall have the
          authority to act in the name or on behalf of or otherwise to bind the other in any way (including, but not
          limited to, the making of any representation or warranty, the assumption of any obligation or liability and
          the exercise of any right or power).
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>25. Third Party Rights</ParagraphTitle>
      <Paragraph>
        <NumberColumn>25.1</NumberColumn>
        <TextColumn>
          Except for the rights of the Institutions under clauses 3.3 and 3.4 to inform Enroly that they have terminated
          Your appointment to provide the Agency Services, these TCs for Agents and the Contract do not confer any
          rights on any person or party (other than the parties to the Contract and, where applicable, their successors
          and permitted assigns) pursuant to the Contracts (Rights of Third Parties) Act 1999.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>26. Notices</ParagraphTitle>
      <Paragraph>
        <NumberColumn>26.1</NumberColumn>
        <TextColumn>
          Any notice required to be given under these TCs for Agents shall be in writing and sent via CAS Shield
          Software or via email.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>27. Governing Law and Jurisdiction</ParagraphTitle>
      <Paragraph>
        <NumberColumn>27.1</NumberColumn>
        <TextColumn>
          These TCs for Agents and the Contract and any dispute or claim arising out of or in connection with it or its
          subject matter or formation (including non-contractual disputes or claims) shall be governed by and construed
          in accordance with the law of England and Wales.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>27.2</NumberColumn>
        <TextColumn>
          Each party irrevocably agrees that the courts of England and Wales shall have exclusive jurisdiction to settle
          any dispute or claim arising out of or in connection with these TCs for Agents, the Contract or their subject
          matter or formation (including non-contractual disputes or claims).
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>28. Interpretation</ParagraphTitle>
      <Paragraph>
        <NumberColumn>28.1</NumberColumn>
        <TextColumn>
          The headings in these TCs for Agents agreement do not affect its interpretation. Except where the context
          otherwise requires, references to clauses and schedules are to clauses and schedules of these TCs for Agents.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>28.2</NumberColumn>
        <TextColumn>References to Enroly and to You include their permitted successors and assigns.</TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>28.3</NumberColumn>
        <TextColumn>
          References to statutory provisions include those statutory provisions as amended or re-enacted.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>28.4</NumberColumn>
        <TextColumn>References to one gender includes a reference to the other genders.</TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>28.5</NumberColumn>
        <TextColumn>
          References to "including" or "includes" shall be deemed to have the words "without limitation" inserted after
          them.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>27.6</NumberColumn>
        <TextColumn>Words in the singular include the plural and those in the plural include the singular.</TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>27.7</NumberColumn>
        <TextColumn>
          A person includes a natural person, corporate, statutory body or unincorporated body (whether or not having
          separate legal personality).
        </TextColumn>
      </Paragraph>
      <SectionTitle>Enroly Data Protection and Data Sharing Policy</SectionTitle>
      <ParagraphTitle>1. Scope of the Enroly Data Protection and Data Sharing Policy</ParagraphTitle>
      <Paragraph>
        <NumberColumn>1.1</NumberColumn>
        <TextColumn>
          The Enroly Data Protection and Data Sharing Policy (“Data Policy”) sets out how Enroly shall deal with
          Personal Data of Students and apply to any and all services provided by Enroly and to the Agency Services
          (“Services”).
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>2. Your additional obligations under this Data Policy</ParagraphTitle>
      <Paragraph>
        <NumberColumn>2.1</NumberColumn>
        <TextColumn>
          If, in connection with the Services, You collect, hold or Process Personal Data and/or if Enroly shares
          Personal Data with You, You shall be a Data Controller and/or Data Processor and Enroly’s obligations set out
          in this Data Policy shall apply to You.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>2.2</NumberColumn>
        <TextColumn>
          If You have Your own data protection and data sharing policy and provided that Enroly agrees that such policy
          is substantially compatible with this Data Policy, You and Enroly may agree that Your policy shall apply to
          Your obligations with regards to Your collection, holding and Processing of Personal Data in connection with
          the Services.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>3. Definitions</ParagraphTitle>
      <Paragraph>
        <NumberColumn>3.1</NumberColumn>
        <TextColumn>The following definitions apply to this Data Policy:</TextColumn>
      </Paragraph>
      {constructTable(definitions)}
      <ParagraphTitle>4. Key Principles and Assumptions</ParagraphTitle>
      <Paragraph>
        <NumberColumn>4.1</NumberColumn>
        <TextColumn>
          This Data Policy sets out Enroly’s obligations regarding the collection, Processing, transfer, storage, and
          disposal of Personal Data.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>4.2</NumberColumn>
        <TextColumn>
          Enroly is committed to the correct, lawful, and fair handling of all Personal Data, and of respecting the
          legal rights, privacy, and trust of all individuals with whom it deals.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>4.3</NumberColumn>
        <TextColumn>
          Enroly confirms that this Data Policy shall be followed by its employees, agents, contractors, or other
          parties working on its behalf.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>4.4</NumberColumn>
        <TextColumn>
          The provision of the Services may require that Enroly controls and/or Processes Personal Data.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>4.5</NumberColumn>
        <TextColumn>
          When Enroly is a Data Controller and/or Data Processor in connection with or as a result of the supply of the
          Services to You, Enroly shall ensure that any Personal Data handled by Enroly must be:
          <SubParagraph>
            <NumberColumn>4.5.1</NumberColumn>
            <TextColumn>
              Processed lawfully, fairly, and in a transparent manner in relation to the Data Subject;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>4.5.2</NumberColumn>
            <TextColumn>
              collected for specified, explicit, and legitimate purposes and not further Processed in a manner that is
              incompatible with those purposes;
              <SubParagraph>
                <NumberColumn>4.5.2.1</NumberColumn>
                <TextColumn>
                  Further Processing for archiving purposes in the public interest, scientific or historical research
                  purposes or statistical purposes shall not be considered to be incompatible with the initial purposes;
                </TextColumn>
              </SubParagraph>
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>4.5.3</NumberColumn>
            <TextColumn>
              adequate, relevant, and limited to what is necessary in relation to the purposes for which it is
              Processed;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>4.5.4</NumberColumn>
            <TextColumn>
              accurate and, where necessary, kept up to date.
              <SubParagraph>
                <NumberColumn>4.5.4.1</NumberColumn>
                <TextColumn>
                  Every reasonable step must be taken to ensure that Personal Data that is inaccurate, having regard to
                  the purposes for which it is Processed, is erased, or rectified without delay;
                </TextColumn>
              </SubParagraph>
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>4.5.5</NumberColumn>
            <TextColumn>
              kept in a form which permits identification of Data Subjects for no longer than is necessary for the
              purposes for which the Personal Data is Processed.
              <SubParagraph>
                <NumberColumn>4.5.5.1</NumberColumn>
                <TextColumn>
                  Personal Data may be stored for longer periods insofar as the Personal Data will be Processed solely
                  for archiving purposes in the public interest, scientific or historical research purposes, or
                  statistical purposes, subject to implementation of the appropriate technical and organisational
                  measures required by the Data Protection Law in order to safeguard the rights and freedoms of the Data
                  Subject;
                </TextColumn>
              </SubParagraph>
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>4.5.6</NumberColumn>
            <TextColumn>
              Processed in a manner that ensures appropriate security of the Personal Data, including protection against
              unauthorised or unlawful Processing and against accidental loss, destruction, or damage, using appropriate
              technical or organisational measures.
            </TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>4.6</NumberColumn>
        <TextColumn>
          When Enroly is a Data Controller and/or Data Processor in connection with or as a result of the supply of the
          Services to You, Enroly shall ensure that the following rights of the Data Subjects set out in the Data
          Protection Law are observed:
          <SubParagraph>
            <NumberColumn>4.6.1</NumberColumn>
            <TextColumn>right to be informed;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>4.6.2</NumberColumn>
            <TextColumn>right of access;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>4.6.3</NumberColumn>
            <TextColumn>right to rectification;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>4.6.4</NumberColumn>
            <TextColumn>right to erasure (also known as the ‘right to be forgotten’);</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>4.6.5</NumberColumn>
            <TextColumn>right to restrict Processing;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>4.6.6</NumberColumn>
            <TextColumn>right to data portability;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>4.6.7</NumberColumn>
            <TextColumn>right to object; and</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>4.6.8</NumberColumn>
            <TextColumn>rights with respect to automated decision-making and profiling.</TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>5. Specified, Explicit, and Legitimate Purposes</ParagraphTitle>
      <Paragraph>
        <NumberColumn>5.1</NumberColumn>
        <TextColumn>
          When providing the Services to you, Enroly may collect, hold and/or Process the following Personal Data for
          the following purposes.
        </TextColumn>
      </Paragraph>

      {constructTable(purposes)}

      <Paragraph>
        <NumberColumn>5.2</NumberColumn>
        <TextColumn>
          Enroly may also collect, hold and Process Personal Data for other purposes expressly permitted by the Data
          Protection Law. This may include purposes not directly related to the provisions of the Services to You.
          Enroly will never use Personal Data to facilitate third-party marketing.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>5.3</NumberColumn>
        <TextColumn>
          Enroly shall ensure that Data Subjects are at all times kept informed of the purpose(s) for which Enroly uses
          their Personal Data.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>6. Consent</ParagraphTitle>
      <Paragraph>
        <NumberColumn>6.1</NumberColumn>
        <TextColumn>
          Enroly may also rely upon Consent as the lawful basis for collecting, holding, and/or Processing Personal
          Data.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>6.2</NumberColumn>
        <TextColumn>
          Consent may take the form of a statement or a positive action. Silence, pre-ticked boxes, or inactivity are
          unlikely to amount to Consent.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>6.3</NumberColumn>
        <TextColumn>
          Where Consent is given in a document which includes other matters, the section dealing with Consent must be
          kept clearly separate from such other matters.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>6.4</NumberColumn>
        <TextColumn>
          Data Subjects shall be free to withdraw Consent at any time and it must be made easy for them to do so. If a
          Data Subject withdraws consent, their request must be honoured promptly.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>6.5</NumberColumn>
        <TextColumn>
          If Personal Data is to be Processed for a different purpose that is incompatible with the purpose(s) for which
          that Personal Data was originally collected and that was not disclosed to the Data Subjects when they first
          provided their Consent, Consent to the new purpose(s) may need to be obtained from the Data Subjects.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>6.6</NumberColumn>
        <TextColumn>
          If Sensitive Personal Data is Processed, Enroly shall normally rely on a lawful basis other than Consent. If
          Consent is relied upon, the Data Subjects in question must be issued with a suitable privacy notice in order
          to capture their Consent.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>6.7</NumberColumn>
        <TextColumn>
          In all cases where Consent is relied upon as the lawful basis for collecting, holding, and/or Processing
          Personal Data, Enroly shall keep records of all Consents obtained in order to ensure that Enroly has complied
          with Consent requirements.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>7. Accuracy of Data and Keeping Data Up-to-Date</ParagraphTitle>
      <Paragraph>
        <NumberColumn>7.1</NumberColumn>
        <TextColumn>
          Enroly shall ensure that all Personal Data it collects, holds or Processes is kept accurate and up-to-date.
          This obligations include the obligation to rectify the Personal Data at the Data Subjects’ request.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>7.2</NumberColumn>
        <TextColumn>
          The accuracy of Personal Data shall be checked when it is collected and at regular intervals thereafter. If
          any Personal Data is found to be inaccurate or out-of-date, all reasonable steps will be taken without delay
          to amend or erase that Personal Data, as appropriate.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>8. Adequate, Relevant and Limited Data Processing</ParagraphTitle>
      <Paragraph>
        <NumberColumn>8.1</NumberColumn>
        <TextColumn>
          Enroly will only collect, hold and Process Personal Data for and to the extent necessary for the specific
          purpose(s) set out in clause 4 of this Data Policy. Personal Data hold by Enroly shall not be Processed for
          any unrelated reasons.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>8.2</NumberColumn>
        <TextColumn>
          Employees, agents, contractors, or other parties working on behalf of Enroly may collect Personal Data only to
          the extent required for the performance of their job duties and in accordance with this Data Policy.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>9. Data Protection Impact Assessments and Privacy by Design</ParagraphTitle>
      <Paragraph>
        <NumberColumn>9.1</NumberColumn>
        <TextColumn>
          In accordance with the privacy by design principles, Enroly shall carry out data protection impact assessments
          for any and all new projects and/or new uses of Personal Data which involve the use of new technologies and
          where the Processing involved is likely to result in a high risk to the rights and freedoms of Data Subjects.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>9.2</NumberColumn>
        <TextColumn>
          Enroly shall follow the principles of privacy by design at all times when collecting, holding, and Processing
          Personal Data. When following the principles of privacy by design, Enroly shall take into consideration:
          <SubParagraph>
            <NumberColumn>9.2.1</NumberColumn>
            <TextColumn>
              the nature, scope, context, and purpose(s) of the collection, holding, and Processing of the Personal
              Data;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>9.2.2</NumberColumn>
            <TextColumn>
              the state of the art of all relevant technical and organisational measures to be taken to protect Personal
              Data in accordance with the Data Protection Law;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>9.2.3</NumberColumn>
            <TextColumn>the cost of implementing such measures; and</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>9.2.4</NumberColumn>
            <TextColumn>
              the risks posed to Data Subjects and to Enroly, including their likelihood and severity.
            </TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>9.3</NumberColumn>
        <TextColumn>
          These data protection impact assessments shall be overseen by the Data Protection Contact and shall address:
          <SubParagraph>
            <NumberColumn>9.3.1</NumberColumn>
            <TextColumn>the type(s) of Personal Data that will be collected, held, and Processed;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>9.3.2</NumberColumn>
            <TextColumn>the purpose(s) for which Personal Data is to be used;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>9.3.3</NumberColumn>
            <TextColumn>how Personal Data is to be used;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>9.3.4</NumberColumn>
            <TextColumn>Enroly’s objectives;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>9.3.5</NumberColumn>
            <TextColumn>the parties (internal and/or external) to be consulted;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>9.3.6</NumberColumn>
            <TextColumn>
              the necessity and proportionality of the Personal Data Processing with respect to the purpose(s) for which
              it is being Processed;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>9.3.7</NumberColumn>
            <TextColumn>risks posed to Data Subjects;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>9.3.8</NumberColumn>
            <TextColumn>risks posed both within and to Enroly; and</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>9.3.9</NumberColumn>
            <TextColumn>proposed measures to minimise and handle identified risks.</TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>10. Secure Processing</ParagraphTitle>
      <Paragraph>
        <NumberColumn>10.1</NumberColumn>
        <TextColumn>
          Enroly shall ensure that all Personal Data collected, held and Processed is kept secure and protected against
          unauthorised or unlawful Processing and against accidental loss, destruction, or damage.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>10.2</NumberColumn>
        <TextColumn>
          All technical and organisational measures taken to protect Personal Data shall be regularly reviewed and
          evaluated to ensure their ongoing effectiveness and the continued security of Personal Data.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>10.3</NumberColumn>
        <TextColumn>
          Enroly shall maintain data security at all times by protecting the confidentiality, integrity, and
          availability of all Personal Data as follows:
          <SubParagraph>
            <NumberColumn>10.3.1</NumberColumn>
            <TextColumn>
              only those with a genuine need to access and use Personal Data and who are authorised to do so may access
              and use it;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>10.3.2</NumberColumn>
            <TextColumn>
              Personal Data must be accurate and suitable for the purpose(s) for which it is collected, held, and
              Processed; and
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>10.3.3</NumberColumn>
            <TextColumn>
              authorised users must always be able to access the Personal Data as required for the authorised
              purpose(s).
            </TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>10.4</NumberColumn>
        <TextColumn>
          The technical and organisational measures that Enroly will take to ensure compliance with this clause 7 are
          set out in Schedule 1 of this Data Policy.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>11. Accountability and Record-Keeping</ParagraphTitle>
      <Paragraph>
        <NumberColumn>11.1</NumberColumn>
        <TextColumn>
          Enroly shall ensure that all employees, agents, contractors, or other parties working on its behalf and
          collecting, holding or Processing Personal Data as part of such work are given appropriate training in data
          protection and privacy, this Data Policy and Data Protection Law.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>11.2</NumberColumn>
        <TextColumn>
          Enroly’s compliance with this Data Policy and the Data Protection Law shall be regularly reviewed and
          evaluated by means of data protection audits.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>11.3</NumberColumn>
        <TextColumn>
          Enroly shall keep written internal records of all Personal Data collection, holding, and Processing. These
          records shall include:
          <SubParagraph>
            <NumberColumn>11.3.1</NumberColumn>
            <TextColumn>Enroly’s name and details;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>11.3.2</NumberColumn>
            <TextColumn>Enroly’s Data Protection Contact;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>11.3.3</NumberColumn>
            <TextColumn>
              any applicable third-party data transfers (including Data Processors And Other Data Controllers with whom
              Personal Data is shared);
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>11.3.4</NumberColumn>
            <TextColumn>the purposes for which Enroly collects, holds, and Processes Personal Data;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>11.3.5</NumberColumn>
            <TextColumn>
              Enroly’s legal basis or bases for collecting, holding, and Processing Personal Data;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>11.3.6</NumberColumn>
            <TextColumn>
              details of the categories of Personal Data collected, held, and Processed by Enroly;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>11.3.7</NumberColumn>
            <TextColumn>the categories of Data Subject to which that Personal Data relates;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>11.3.8</NumberColumn>
            <TextColumn>details of any transfers of Personal Data to non-EEA countries;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>11.3.9</NumberColumn>
            <TextColumn>
              details of all mechanisms and security safeguards related to the transfer of Personal Data to non-EEA
              countries;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>11.3.10</NumberColumn>
            <TextColumn>details of how long Enroly will retain the Personal Data;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>11.3.11</NumberColumn>
            <TextColumn>details of Personal Data storage, including location(s);</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>11.3.12</NumberColumn>
            <TextColumn>
              detailed descriptions of all technical and organisational measures taken by the Enroly to ensure the
              security of Personal Data.
            </TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>12. Data Retention</ParagraphTitle>
      <Paragraph>
        <NumberColumn>12.1</NumberColumn>
        <TextColumn>
          Unless otherwise agreed with You in writing or required under UK Visas and Immigration requirements, Enroly
          shall retain Personal Data up to the periods set out in Schedule 2 to this Data Policy.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>13. Keeping Data Subjects Informed</ParagraphTitle>
      <Paragraph>
        <NumberColumn>13.1</NumberColumn>
        <TextColumn>
          Where Enroly collects Personal Data directly from Data Subjects, Enroly shall inform the Data Subject about
          the purpose(s) of collecting, holding and Processing such Personal Data at the time of collection.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>13.2</NumberColumn>
        <TextColumn>
          Where Enroly obtains Personal Data from You or a third party, Enroly shall inform the Data Subject about the
          purpose(s) of collecting, holding and Processing such Personal Data:
          <SubParagraph>
            <NumberColumn>13.2.1</NumberColumn>
            <TextColumn>
              if the Personal Data is used to communicate with the Data Subject, when Enroly first communicates with the
              Data Subject; or
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>13.2.2</NumberColumn>
            <TextColumn>
              if the Personal Data is to be transferred to another party, before that transfer is made; or
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>13.2.3</NumberColumn>
            <TextColumn>
              as soon as reasonably possible and in any event not more than one month after the Personal Data is
              obtained.
            </TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>13.3</NumberColumn>
        <TextColumn>
          The following information shall be provided to the Data Subjects in the form of a privacy notice:
          <SubParagraph>
            <NumberColumn>13.3.1</NumberColumn>
            <TextColumn>Enroly’s details (including its Data Protection Contact);</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>13.3.2</NumberColumn>
            <TextColumn>the purpose(s) for which the Personal Data is being collected, held and Processed;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>13.3.3</NumberColumn>
            <TextColumn>the lawful basis justifying the collection, holding and Processing;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>13.3.4</NumberColumn>
            <TextColumn>
              where applicable, the legitimate interests upon which Enroly’s is justifying its collection, holding and
              Processing of the Personal Data;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>13.3.5</NumberColumn>
            <TextColumn>
              where the Personal Data is not obtained directly from the Data Subject, the categories of Personal Data
              collected, held and Processed;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>13.3.6</NumberColumn>
            <TextColumn>
              where the Personal Data is not obtained directly from the Data Subject, details about the source of that
              Personal Data;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>13.3.7</NumberColumn>
            <TextColumn>
              where the Personal Data is to be transferred to one or more third parties, details of those parties;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>13.3.8</NumberColumn>
            <TextColumn>
              where the Personal Data is to be transferred to a third party that is located outside of the EEA, details
              of that transfer, including but not limited to the safeguards in place;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>13.3.9</NumberColumn>
            <TextColumn>details of applicable data retention periods;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>13.3.10</NumberColumn>
            <TextColumn>details of the Data Subjects’ rights under the Data Protection Law;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>13.3.11</NumberColumn>
            <TextColumn>
              details of the Data Subjects’ right to withdraw their Consent to Enroly’s Processing of their Personal
              Data at any time;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>13.3.12</NumberColumn>
            <TextColumn>
              details of the Data Subjects’ right to complain to the Information Commissioner’s Office (the “supervisory
              authority” under the Data Protection Law);
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>13.3.13</NumberColumn>
            <TextColumn>
              where applicable, details of any legal or contractual requirement or obligation necessitating the
              collection and Processing of the Personal Data and details of any consequences of failing to provide it;
              and
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>13.3.14</NumberColumn>
            <TextColumn>
              details of any automated decision-making or profiling that will take place using the Personal Data,
              including information on how decisions will be made, the significance of those decisions, and any
              consequences.
            </TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>14. Data Subject Access</ParagraphTitle>
      <Paragraph>
        <NumberColumn>14.1</NumberColumn>
        <TextColumn>
          Data Subjects may make subject access requests (“SARs”) at any time to find out more about the Personal Data
          which Enroly holds about them, what it is doing with that Personal Data, and why.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>14.2</NumberColumn>
        <TextColumn>
          Responses to SARs must normally be made within one month of receipt, however, this may be extended by up to
          two months if the SAR is complex and/or numerous requests are made. If such additional time is required, the
          Data Subject shall be informed.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>14.3</NumberColumn>
        <TextColumn>All SARs received shall be handled by the Data Protection Contact.</TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>14.4</NumberColumn>
        <TextColumn>
          Enroly does not charge a fee for the handling of normal SARs. Enroly reserves the right to charge reasonable
          fees for additional copies of information that has already been supplied to a Data Subject, and for requests
          that are manifestly unfounded or excessive, particularly where such requests are repetitive.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>15. Rectification of Personal Data</ParagraphTitle>
      <Paragraph>
        <NumberColumn>15.1</NumberColumn>
        <TextColumn>
          Data subjects have the right to require Enroly to rectify any of their Personal Data that is inaccurate or
          incomplete.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>15.2</NumberColumn>
        <TextColumn>
          Enroly shall rectify the Personal Data in question, and inform the Data Subject of that rectification, within
          one month of the Data Subject informing Enroly of the issue. The period can be extended by up to two months in
          the case of complex requests. If such additional time is required, the Data Subject shall be informed.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>15.3</NumberColumn>
        <TextColumn>
          In the event that any affected Personal Data has been disclosed to third parties, those parties shall be
          informed of any rectification that must be made to that Personal Data.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>16. Erasure of Personal Data</ParagraphTitle>
      <Paragraph>
        <NumberColumn>16.1</NumberColumn>
        <TextColumn>
          Data Subjects have the right to request that Enroly erases the Personal Data it holds about them in the
          following circumstances:
          <SubParagraph>
            <NumberColumn>16.1.1</NumberColumn>
            <TextColumn>
              it is no longer necessary for Enroly to hold that Personal Data with respect to the purpose(s) for which
              it was originally collected or Processed;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>16.1.2</NumberColumn>
            <TextColumn>
              the Data Subjects wish to withdraw their Consent to Enroly holding and Processing their Personal Data;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>16.1.3</NumberColumn>
            <TextColumn>
              the Data Subjects object to Enroly holding and Processing their Personal Data (and there is no overriding
              legitimate interest to allow Enroly to continue doing so);
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>16.1.4</NumberColumn>
            <TextColumn>the Personal Data has been Processed unlawfully; or</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>16.1.5</NumberColumn>
            <TextColumn>
              the Personal Data needs to be erased in order to comply with a particular legal obligation.
            </TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>16.2</NumberColumn>
        <TextColumn>
          Unless Enroly has reasonable grounds to refuse to erase Personal Data, all requests for erasure shall be
          complied with. The Data Subject informed of the erasure within one month of receipt of the Data Subject’s
          request. The period can be extended by up to two months in the case of complex requests. If such additional
          time is required, the Data Subject shall be informed.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>16.3</NumberColumn>
        <TextColumn>
          In the event that any Personal Data that is to be erased in response to a Data Subject’s request has been
          disclosed to third parties, those parties shall be informed of the erasure (unless it is impossible or would
          require disproportionate effort to do so).
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>17. Restriction of Personal Data Processing</ParagraphTitle>
      <Paragraph>
        <NumberColumn>17.1</NumberColumn>
        <TextColumn>
          Data Subjects may request that Enroly ceases Processing the Personal Data it holds about them. If a Data
          Subject makes such a request, Enroly shall retain only the amount of Personal Data concerning that Data
          Subject (if any) that is necessary to ensure that the Personal Data in question is not Processed further.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>17.2</NumberColumn>
        <TextColumn>
          In the event that any affected Personal Data has been disclosed to third parties, those parties shall be
          informed of the applicable restrictions on Processing it (unless it is impossible or would require
          disproportionate effort to do so).
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>18. Data Portability</ParagraphTitle>
      <Paragraph>
        <NumberColumn>18.1</NumberColumn>
        <TextColumn>
          Where Data Subjects have given their Consent to Enroly to Process their Personal Data in such a manner, or the
          Processing is otherwise required for the performance of a contract between Enroly and the Data Subject, Data
          Subjects have the right, under the Data Protection Law, to receive a copy of their Personal Data and to use it
          for other purposes (namely transmitting it to other Data Controllers).
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>18.2</NumberColumn>
        <TextColumn>
          To facilitate the right of data portability, Enroly shall make available all applicable Personal Data to Data
          Subjects in JSON (JavaScript Object Notation).
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>18.3</NumberColumn>
        <TextColumn>You are also permitted to download the Personal Data to Your secure servers.</TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>18.4</NumberColumn>
        <TextColumn>
          Where technically feasible and if requested by a Data Subject, Personal Data shall be sent directly to the
          required Data Controller.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>18.5</NumberColumn>
        <TextColumn>
          All requests for copies of Personal Data shall be complied with within one month of the Data Subject’s
          request. The period can be extended by up to two months in the case of complex or numerous requests. If such
          additional time is required, the Data Subject shall be informed.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>19. Objections to Personal Data Processing</ParagraphTitle>
      <Paragraph>
        <NumberColumn>19.1</NumberColumn>
        <TextColumn>
          Where Data Subjects object to Enroly Processing their Personal Data based on its legitimate interests, Enroly
          shall cease such Processing immediately, unless it can be demonstrated that Enroly’s legitimate grounds for
          such Processing override the Data Subject’s interests, rights, and freedoms, or that the Processing is
          necessary for the conduct of legal claims.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>19.2</NumberColumn>
        <TextColumn>
          Where a Data Subjects object to Enroly Processing their Personal Data for scientific and/or historical
          research and statistics purposes, the Data Subject must, under the Data Protection Law, “demonstrate grounds
          relating to his or her particular situation”. Enroly is not required to comply with such Data Subjects’
          request if the research is necessary for the performance of a task carried out for reasons of public interest.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>20. Automated Processing, Automated Decision-Making, and Profiling</ParagraphTitle>
      <Paragraph>
        <NumberColumn>20.1</NumberColumn>
        <TextColumn>
          Enroly Processes Personal Data using automated and semi-automated means that will collect Personal Data from
          Students and automatically allocate that data to specific groups, lists etc as required for You. The referred
          automated and semi-automated means use Personal Data as follows:
          <SubParagraph>
            <NumberColumn>20.1.1</NumberColumn>
            <TextColumn>
              the Students will have their Personal Data gathered either directly or indirectly by Enroly, and this
              Personal Data will be profiled in order that You can make informed decisions about offers of places at
              Your courses;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>20.1.2</NumberColumn>
            <TextColumn>
              the Services include a ‘smart form’ that identifies common mistakes and potential risk factors as it
              collates data required by You;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>20.1.3</NumberColumn>
            <TextColumn>
              Students are informed on how to correct common mistakes and prepare documents appropriately; and
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>20.1.4</NumberColumn>
            <TextColumn>
              thereafter, You are informed of any potential risk factors provided by Students’ answers.
            </TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>20.2</NumberColumn>
        <TextColumn>
          Enroly does not use Personal Data for profiling, as the automated and semi-automated means Enroly uses simply
          provides You with more succinct and tailored information. Such means do not to lead to automatic decisions
          being taken on Your behalf. No part of the Services makes automated decisions that impact a Student’s
          enrolment status with You. All decisions are made by Your staff, as appropriate.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>20.3</NumberColumn>
        <TextColumn>
          The activities described in clause 19.1 do not have a legal or similarly significant effect on Data Subjects.
          If they do, Enroly will only conduct them if:
          <SubParagraph>
            <NumberColumn>20.3.1</NumberColumn>
            <TextColumn>the Data Subject has given their explicit consent;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>20.3.2</NumberColumn>
            <TextColumn>the Processing is authorised by law; or</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>20.3.3</NumberColumn>
            <TextColumn>
              the Processing is necessary for the entry into, or performance of, a contract between Enroly and the Data
              Subject.
            </TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>20.4</NumberColumn>
        <TextColumn>
          If Sensitive Personal Data is to be Processed by automated or semi-automated means, such Processing can only
          be carried out if:
          <SubParagraph>
            <NumberColumn>20.4.1</NumberColumn>
            <TextColumn>the Data Subject has given their explicit consent; or</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>20.4.2</NumberColumn>
            <TextColumn>the Processing is necessary for reasons of substantial public interest.</TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>20.5</NumberColumn>
        <TextColumn>
          Where decisions are to be based solely on automated Processing (including profiling), Data Subjects have the
          right to object, to challenge such decisions, request human intervention, to express their own point of view,
          and to obtain an explanation of the decision from Enroly. Data Subjects must be explicitly informed of this
          right at the first point of contact.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>20.6</NumberColumn>
        <TextColumn>
          Enroly must provide clear information to Data Subjects explaining the logic involved in the decision-making or
          profiling, and the significance and envisaged consequences of the decision or decisions.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>20.7</NumberColumn>
        <TextColumn>
          When Personal Data is used for any form of automated Processing, automated decision-making, or profiling, the
          following shall apply:
          <SubParagraph>
            <NumberColumn>20.7.1</NumberColumn>
            <TextColumn>appropriate mathematical or statistical procedures shall be used;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>20.7.2</NumberColumn>
            <TextColumn>
              technical and organisational measures shall be implemented to minimise the risk of errors. If errors
              occur, such measures must enable them to be easily corrected; and
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>20.7.3</NumberColumn>
            <TextColumn>
              all Personal Data to be Processed in this manner shall be secured in order to prevent discriminatory
              effects arising.
            </TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>21. Transferring Personal Data to a Country Outside the EEA</ParagraphTitle>
      <Paragraph>
        <NumberColumn>21.1</NumberColumn>
        <TextColumn>
          Enroly may from time to time transfer (‘transfer’ includes making available remotely) Personal Data to
          countries outside of the EEA.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>21.2</NumberColumn>
        <TextColumn>
          The transfer of Personal Data to a country outside of the EEA shall take place only if one or more of the
          following applies:
          <SubParagraph>
            <NumberColumn>21.2.1</NumberColumn>
            <TextColumn>
              the transfer is to a country, territory, or one or more specific sectors in that country (or an
              international organisation), that the European Commission has determined ensures an adequate level of
              protection for Personal Data;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>21.2.2</NumberColumn>
            <TextColumn>
              the transfer is to a country (or international organisation) which provides appropriate safeguards in the
              form of a legally binding agreement between public authorities or bodies; binding corporate rules;
              standard data protection clauses adopted by the European Commission; compliance with an approved code of
              conduct approved by a supervisory authority (e.g. the Information Commissioner’s Office); certification
              under an approved certification mechanism (as provided for in the Data Protection Law); contractual
              clauses agreed and authorised by the competent supervisory authority; or provisions inserted into
              administrative arrangements between public authorities or bodies authorised by the competent supervisory
              authority;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>21.2.3</NumberColumn>
            <TextColumn>
              the transfer is made with the informed and explicit consent of the relevant Data Subjects;
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>21.2.4</NumberColumn>
            <TextColumn>
              the transfer is necessary for the performance of a contract between the Data Subject and Enroly (or for
              pre-contractual steps taken at the request of the Data Subject);
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>21.2.5</NumberColumn>
            <TextColumn>the transfer is necessary for important public interest reasons;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>21.2.6</NumberColumn>
            <TextColumn>the transfer is necessary for the conduct of legal claims;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>21.2.7</NumberColumn>
            <TextColumn>
              the transfer is necessary to protect the vital interests of the Data Subject or other individuals where
              the Data Subject is physically or legally unable to give their consent; or
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>21.2.8</NumberColumn>
            <TextColumn>
              the transfer is made from a register that, under UK or EU law, is intended to provide information to the
              public and which is open for access by the public in general or otherwise to those who are able to show a
              legitimate interest in accessing the register.
            </TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>22. Data Breach Notification</ParagraphTitle>
      <Paragraph>
        <NumberColumn>22.1</NumberColumn>
        <TextColumn>All Personal Data breaches must be reported immediately to the Data Protection Contact.</TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>22.2</NumberColumn>
        <TextColumn>
          If an employee, agent, contractor, or other party working on behalf of Enroly becomes aware of or suspects
          that a Personal Data breach has occurred, they must not attempt to investigate it themselves. Any and all
          evidence relating to the Personal Data breach in question should be carefully retained.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>22.3</NumberColumn>
        <TextColumn>
          If a Personal Data breach occurs and that breach is likely to result in a risk to the rights and freedoms of
          Data Subjects (e.g. financial loss, breach of confidentiality, discrimination, reputational damage, or other
          significant social or economic damage), the Data Protection Contact must ensure that the Information
          Commissioner’s Office is informed of the breach without delay, and in any event, within 72 hours after having
          become aware of it.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>22.4</NumberColumn>
        <TextColumn>
          In the event that a Personal Data breach is likely to result in a high risk to the rights and freedoms of Data
          Subjects, the Data Protection Contact must ensure that all affected Data Subjects are informed of the breach
          directly and without undue delay.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>22.5</NumberColumn>
        <TextColumn>
          Data breach notifications shall include the following information:
          <SubParagraph>
            <NumberColumn>22.5.1</NumberColumn>
            <TextColumn>the categories and approximate number of Data Subjects concerned;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>22.5.2</NumberColumn>
            <TextColumn>the categories and approximate number of Personal Data records concerned;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>22.5.3</NumberColumn>
            <TextColumn>
              the name and contact details of the Data Protection Contact (or other contact point where more information
              can be obtained);
            </TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>22.5.4</NumberColumn>
            <TextColumn>the likely consequences of the breach;</TextColumn>
          </SubParagraph>
          <SubParagraph>
            <NumberColumn>22.5.5</NumberColumn>
            <TextColumn>
              details of the measures taken, or proposed to be taken, by Enroly to address the breach including, where
              appropriate, measures to mitigate its possible adverse effects.
            </TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>23. Implementation of Policy</ParagraphTitle>
      <Paragraph>
        <NumberColumn>23.1</NumberColumn>
        <TextColumn>
          This Policy shall be deemed effective as of 1st March 2019. No part of this Policy shall have retroactive
          effect and shall thus apply only to matters occurring on or after this date.
          <SubParagraph>
            <TextColumn>
              This Policy has been approved and authorised by:
              <SubParagraph>
                <DetailColumn>Name:</DetailColumn>
                <TextColumn>Mike Stone</TextColumn>
              </SubParagraph>
              <SubParagraph>
                <DetailColumn>Position:</DetailColumn>
                <TextColumn>Data Protection Contact</TextColumn>
              </SubParagraph>
              <SubParagraph>
                <DetailColumn>Last Reviewed:</DetailColumn>
                <TextColumn>15th January 2026</TextColumn>
              </SubParagraph>
              <SubParagraph>
                <DetailColumn>Next Review due:</DetailColumn>
                <TextColumn>15th January 2027</TextColumn>
              </SubParagraph>
            </TextColumn>
          </SubParagraph>
        </TextColumn>
      </Paragraph>
      <SectionTitle>
        Schedule 1<br />
        Technical and Organisational Measures
      </SectionTitle>
      <ParagraphTitle>1. Data Security - Transferring Personal Data and Communications</ParagraphTitle>
      <Paragraph>
        <TextColumn>
          Enroly shall ensure that the following measures are taken with respect to all communications and other
          transfers involving Personal Data:
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>1.1</NumberColumn>
        <TextColumn>All emails containing Personal Data must be marked “confidential”;</TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>1.2</NumberColumn>
        <TextColumn>Personal Data may be transmitted over secure networks only;</TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>1.3</NumberColumn>
        <TextColumn>
          Transmission of Personal Data over unsecured networks is not permitted in any circumstances;
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>1.4</NumberColumn>
        <TextColumn>
          Where Personal Data is transferred to other systems and bodies, it will be managed through secured API
          endpoints, and data will be encrypted in transmit, where required;
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>1.5</NumberColumn>
        <TextColumn>
          Personal Data may not be transmitted over a wireless network if there is a wired alternative that is
          reasonably practicable;
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>1.6</NumberColumn>
        <TextColumn>
          Personal Data contained in the body of an email, whether sent or received, should be copied from the body of
          that email and stored securely. The email itself should be deleted. All temporary files associated therewith
          should also be deleted;
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>1.7</NumberColumn>
        <TextColumn>
          Where Personal Data is to be sent by facsimile transmission the recipient should be informed in advance of the
          transmission and should be waiting by the fax machine to receive the data;
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>1.8</NumberColumn>
        <TextColumn>
          Where Personal Data is to be transferred in hardcopy form it should be passed directly to the recipient; and
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>1.9</NumberColumn>
        <TextColumn>
          All Personal Data to be transferred physically, whether in hardcopy form or on removable electronic media
          shall be transferred in a suitable container marked “confidential”.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>2. Data Security - Storage</ParagraphTitle>
      <Paragraph>
        <TextColumn>
          Enroly shall ensure that the following measures are taken with respect to the storage of Personal Data:
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>2.1</NumberColumn>
        <TextColumn>
          All electronic copies of Personal Data should be stored securely using passwords and data encryption;
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>2.2</NumberColumn>
        <TextColumn>
          All hardcopies of Personal Data, along with any electronic copies stored on physical, removable media should
          be stored securely in a locked box, drawer, cabinet, or similar;
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>2.3</NumberColumn>
        <TextColumn>
          All Personal Data stored electronically should be backed up regularly with backups stored. All backups should
          be encrypted;
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>2.4</NumberColumn>
        <TextColumn>
          No Personal Data should be stored on any mobile device (including, but not limited to, laptops, tablets, and
          smartphones), whether such device belongs to Enroly or otherwise; and
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>2.5</NumberColumn>
        <TextColumn>
          No Personal Data should be transferred to any device personally belonging to an employee, agent, contractor,
          or other party working on behalf of Enroly and Personal Data may only be transferred to devices belonging to
          agents, contractors, or other parties working on behalf of Enroly where the party in question has agreed to
          comply fully with the letter and spirit of this Policy and of the Data Protection Law (which may include
          demonstrating to Enroly that all suitable technical and organisational measures have been taken).
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>3. Data Security - Disposal</ParagraphTitle>
      <Paragraph>
        <TextColumn>
          When any Personal Data is to be erased or otherwise disposed of for any reason (including where copies have
          been made and are no longer needed), it should be securely deleted and disposed of.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>4. Data Security - Use of Personal Data</ParagraphTitle>
      <Paragraph>
        <TextColumn>
          Enroly shall ensure that the following measures are taken with respect to the use of Personal Data:
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>4.1</NumberColumn>
        <TextColumn>
          No Personal Data may be shared informally and if an employee, agent, contractor, or other party working on
          behalf of Enroly requires access to any Personal Data that they do not already have access to, such access
          should be formally requested from the Data Protection Contact;
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>4.2</NumberColumn>
        <TextColumn>
          No Personal Data may be transferred to any employee, agent, contractor, or other party, whether such parties
          are working on behalf of Enroly or not, without the authorisation of the Data Protection Contact;
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>4.3</NumberColumn>
        <TextColumn>
          Personal data must be handled with care at all times and should not be left unattended or on view to
          unauthorised employees, agents, contractors, or other parties at any time;
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>4.4</NumberColumn>
        <TextColumn>
          If Personal Data is being viewed on a computer screen and the computer in question is to be left unattended
          for any period of time, the user must lock the computer and screen before leaving it; and
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>4.5</NumberColumn>
        <TextColumn>
          Where Personal Data held by Enroly is used for marketing purposes, it shall be the responsibility of the Data
          Protection Contact to ensure that the appropriate consent is obtained and that no Data Subjects have opted
          out, whether directly or via a third-party service such as the TPS.
        </TextColumn>
      </Paragraph>
      <ParagraphTitle>5. Data Security - IT Security</ParagraphTitle>
      <Paragraph>
        <TextColumn>
          Enroly shall ensure that the following measures are taken with respect to IT and information security:
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>5.1</NumberColumn>
        <TextColumn>
          All passwords used to protect Personal Data should be changed regularly and should not use words or phrases
          that can be easily guessed or otherwise compromised. All passwords must contain a combination of uppercase and
          lowercase letters, numbers, and symbols;
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>5.2</NumberColumn>
        <TextColumn>
          Under no circumstances should any passwords be written down or shared between any employees, agents,
          contractors, or other parties working on behalf of Enroly, irrespective of seniority or department. If a
          password is forgotten, it must be reset using the applicable method. IT staff do not have access to passwords;
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>5.3</NumberColumn>
        <TextColumn>
          All software (including, but not limited to, applications and operating systems) shall be kept up-to-date.
          Enroly’s IT staff shall be responsible for installing any and all security-related updates as soon as
          reasonably and practically possible; and
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>5.4</NumberColumn>
        <TextColumn>
          No software may be installed on any Company-owned computer or device without the prior approval of the Data
          Protection Contact.
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>5.5</NumberColumn>
        <TextColumn>Enroly maintains a detailed Information Security Policy that is available upon request.</TextColumn>
      </Paragraph>
      <ParagraphTitle>6. Organisational Measures</ParagraphTitle>
      <Paragraph>
        <TextColumn>
          Enroly shall ensure that the following measures are taken with respect to the collection, holding, and
          Processing of Personal Data:
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>6.1</NumberColumn>
        <TextColumn>
          All employees, agents, contractors, or other parties working on behalf of Enroly shall be made fully aware of
          both their individual responsibilities and Enroly’s responsibilities under Data Protection Law and under this
          Policy, and shall be provided with a copy of this Policy;
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>6.2</NumberColumn>
        <TextColumn>
          Only employees, agents, contractors, or other parties working on behalf of Enroly that need access to, and use
          of, Personal Data in order to carry out their assigned duties correctly shall have access to Personal Data
          held by Enroly;
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>6.3</NumberColumn>
        <TextColumn>
          All sharing of Personal Data shall comply with the information provided to the relevant Data Subjects and, if
          required, the consent of such Data Subjects shall be obtained prior to the sharing of their Personal Data;
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>6.4</NumberColumn>
        <TextColumn>
          All employees, agents, contractors, or other parties working on behalf of Enroly handling Personal Data will
          be appropriately trained to do so;
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>6.5</NumberColumn>
        <TextColumn>
          All employees, agents, contractors, or other parties working on behalf of Enroly handling Personal Data will
          be appropriately supervised;
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>6.6</NumberColumn>
        <TextColumn>
          All employees, agents, contractors, or other parties working on behalf of Enroly handling Personal Data shall
          be required and encouraged to exercise care, caution, and discretion when discussing work-related matters that
          relate to Personal Data, whether in the workplace or otherwise;
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>6.7</NumberColumn>
        <TextColumn>
          Methods of collecting, holding, and Processing Personal Data shall be regularly evaluated and reviewed;
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>6.8</NumberColumn>
        <TextColumn>All Personal Data held by Enroly shall be reviewed periodically;</TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>6.9</NumberColumn>
        <TextColumn>
          The performance of those employees, agents, contractors, or other parties working on behalf of Enroly handling
          Personal Data shall be regularly evaluated and reviewed;
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>6.10</NumberColumn>
        <TextColumn>
          All employees, agents, contractors, or other parties working on behalf of Enroly handling Personal Data will
          be bound to do so in accordance with the principles of Data Protection Law and this Policy by contract;
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>6.11</NumberColumn>
        <TextColumn>
          All agents, contractors, or other parties working on behalf of Enroly handling Personal Data must ensure that
          any and all of their employees who are involved in the Processing of Personal Data are held to the same
          conditions as those relevant employees of Enroly arising out of this Policy and Data Protection Law; and
        </TextColumn>
      </Paragraph>
      <Paragraph>
        <NumberColumn>6.12</NumberColumn>
        <TextColumn>
          Where any agent, contractor or other party working on behalf of Enroly handling Personal Data fails in their
          obligations under this Policy that party shall indemnify and hold harmless Enroly against any costs,
          liability, damages, loss, claims or proceedings which may arise out of that failure.
        </TextColumn>
      </Paragraph>
      <SectionTitle>
        Schedule 2<br />
        Data Retention Policy
      </SectionTitle>

      <TableContainer>{constructTable(dataRetentionPolicy)}</TableContainer>
    </Modal>
  );
};

const ParagraphTitle = (props: IChildren): React.ReactElement => (
  <Heading
    as="h4"
    fontSize="lg"
    fontWeight="medium"
    mt={6}
    mb={2}
    color="purple.500"
    _first={{ mt: 0 }}
    _dark={{ color: 'purple.400' }}
  >
    {props.children}
  </Heading>
);

const SectionTitle = (props: IChildren): React.ReactElement => (
  <Heading as="h3" fontSize="xl" mt={8} mb={3} textAlign="center">
    {props.children}
  </Heading>
);

const Paragraph = (props: IChildren): React.ReactElement => <Flex mb={3}>{props.children}</Flex>;

const SubParagraph = (props: IChildren): React.ReactElement => <Flex my={1}>{props.children}</Flex>;

const NumberColumn = (props: IChildren): React.ReactElement => (
  <Box width="14%" flexShrink="0" color="muted">
    {props.children}
  </Box>
);

const DetailColumn = (props: IChildren): React.ReactElement => (
  <Box width="40%" flexShrink="0" fontWeight="medium" color="muted" pl={8}>
    {props.children}
  </Box>
);

const TextColumn = (props: IChildren): React.ReactElement => <Box flexGrow="1">{props.children}</Box>;

export default AgentTermsModal;
